import type { Cluster } from '@googlemaps/markerclusterer';
import { flatten, isEqual, keyBy, map as lodashMap } from 'lodash';
import { map } from 'rxjs';
import type { Observable } from 'rxjs';

import { assertValue, log } from '@libs/utils';

import type { GoogleMapsAddedMarker } from './google/types';
import type { Coordinate, Rectangle, RectangleAndZoom } from './helpers/geo';
import { trackMapLoad } from './helpers/tracking';
import { ConnectionType } from './directions';
import type { RouteWithDetails, RouteConnection, Route } from './directions';

export type AddedMarker = unknown;

export type MapParent = Element;

export type GestureType = 'auto' | 'cooperative' | 'greedy' | 'none';

export interface Area {
  height: number;
  width: number;
}

export interface MapMoveEvent {
  userInitiated: boolean;
}

export interface MapMovedEvent extends MapMoveEvent {
  newView: Rectangle;
  zoom?: number;
}

export interface MapInitializationOptions {
  gestureHandling: GestureType | undefined;
  locale: string;
  mapControls: MapControl[];
  markerFormatters?: Record<string, (marker: Marker) => HTMLDivElement>;
  center?: Coordinate | Coordinate[];
  clusterMarkerFormatters?: Record<
    string,
    (marker: Marker, cluster: Cluster) => HTMLDivElement
  >;
}
export interface StaticMapOptions {
  size: Area;
  markers?: Marker[];
  autogeneratePaths: boolean;
  pathFormat?: string;
  markerFormatters?: Record<string, string>;
  center?: Coordinate | Coordinate[];
  zoomLevel?: number;
  scale?: 1 | 2;
}

export interface Marker {
  key: string;
  position: Coordinate;
  cssClass?: string;
  title?: string;
  subtitle?: string;
  markerText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  cluster?: boolean;
  useContentFormatter?: string;
  useClusterContentFormatter?: string;
  displayPriority?: number;
}

export interface ClusterOptions {
  radius?: number;
  minPoints?: number;
}

export type MarkerListeners = Partial<
  Record<MarkerEventTriggers, (marker: Marker) => void>
>;

export type MarkerEventTriggers =
  | 'click'
  | 'mouseenter'
  | 'mouseleave'
  | 'touchstart';

export enum MapControl {
  zoomControl,
  mapTypeControl,
  scaleControl,
  streetViewControl,
  rotateControl,
  fullscreenControl,
}

export enum MapIntegrationType {
  GoogleMaps,
}

export type MapIntegrationMarker = {
  [T in MapIntegrationType]: {
    [MapIntegrationType.GoogleMaps]: GoogleMapsAddedMarker;
  }[T];
};

export interface MapDynamicIntegration<
  T extends MapIntegrationType = MapIntegrationType
> {
  createDynamicMapIntegration(
    element: MapParent,
    options?: MapInitializationOptions
  ): Promise<MountedDynamicMap<T>>;
}

export interface MapStaticIntegration {
  createMap(
    element: MapParent,
    options?: StaticMapOptions
  ): Promise<MountedMap>;
}

export interface MountedDynamicMap<T extends MapIntegrationType>
  extends MountedMap {
  viewChanged: Observable<RectangleAndZoom>;
  mapTypeIdChanged: Observable<string>;

  addMarker(
    marker: Marker,
    listeners: MarkerListeners,
    clusterOptions?: ClusterOptions
  ): MapIntegrationMarker[T];
  replaceMarker(
    existingMarker: MapIntegrationMarker[T],
    newMarker: Marker,
    newMarkerListeners: MarkerListeners
  ): MapIntegrationMarker[T];
  removeMarker(marker: MapIntegrationMarker[T]): void;
  showMarkers(markers: MapIntegrationMarker[T][]): Promise<void>;
  clearRoutes(): void;
  addRoute(
    from: Marker,
    to: Marker,
    waypoints: Marker[]
  ): Promise<RouteWithDetails>;
  addLine(from: Coordinate, to: Coordinate): void;
  center(rectangle: Rectangle): Promise<void>;
  getBoundingRectangle(): Rectangle;
  updateAddMarkersBetweenRoutes(addMarkersBetweenRoutes: boolean): void;
}

export interface MountedMap {
  destroy(): void;
}

export enum MapType {
  Dynamic = 'Dynamic',
  Static = 'Static',
}

export enum MapTypeId {
  HYBRID = 'hybrid',
  ROADMAP = 'roadmap',
  SATELLITE = 'satellite',
  TERRAIN = 'terrain',
}

export class StaticMap {
  public integration: MountedMap;

  constructor(integration: MountedMap) {
    this.integration = integration;
  }
}

export class Map<T extends MapIntegrationType = MapIntegrationType> {
  private isMovingMapInCodeCounter = 0;
  private existingMarkers: Record<
    string,
    { marker: Marker; mapMarker: MapIntegrationMarker[T] }
  > = {};

  private existingRoute?: RouteWithDetails;

  constructor(private integration: MountedDynamicMap<T>) {}

  public get viewChanged(): Observable<MapMovedEvent> {
    return this.integration.viewChanged.pipe(
      map((x) => ({
        newView: x.rectangle,
        userInitiated: this.isMovingMapInCodeCounter === 0,
        zoom: x.zoom,
      }))
    );
  }

  public get mapTypeIdChanged(): Observable<string> {
    return this.integration.mapTypeIdChanged;
  }

  public getBoundingRectangle(): Rectangle {
    return this.integration.getBoundingRectangle();
  }

  public updateAddMarkersBetweenRoutes(addMarkersBetweenRoutes: boolean): void {
    this.integration.updateAddMarkersBetweenRoutes(addMarkersBetweenRoutes);
  }

  public async center(rectangle: Rectangle): Promise<void> {
    this.isMovingMapInCodeCounter++;
    await this.integration.center(rectangle);
    this.isMovingMapInCodeCounter--;
  }

  public destroy(): void {
    this.integration.destroy();
  }

  public setMarkers(
    markers: Marker[],
    showAllMarkers: boolean,
    listeners: MarkerListeners,
    clusterOptions?: ClusterOptions
  ): void {
    const indexedNewMarkers = keyBy(
      markers,
      (marker: Marker) => marker.key + marker.cluster
    );

    log('Begin setting markers', indexedNewMarkers);

    let thereAreDifferentMarkers = false;

    for (const markerKey in this.existingMarkers) {
      const marker = this.existingMarkers[markerKey];

      assertValue(marker, 'maps.ts > setMarkers > marker');

      if (!(markerKey + marker.marker.cluster in indexedNewMarkers)) {
        log('Removing marker', { key: markerKey });

        thereAreDifferentMarkers = true;

        this.integration.removeMarker(marker.mapMarker);
        delete this.existingMarkers[markerKey];
      }
    }

    for (const marker of markers) {
      if (!(marker.key in this.existingMarkers)) {
        log('Adding marker', { key: marker.key });

        thereAreDifferentMarkers = true;

        const mapMarker = this.integration.addMarker(
          marker,
          listeners,
          clusterOptions
        );

        this.existingMarkers[marker.key] = { marker, mapMarker };
      } else {
        const existingMarker = this.existingMarkers[marker.key];
        assertValue(existingMarker, 'maps.ts > setMarkers > existingMarker');

        const isDifferentMarkerContent = !isEqual(
          existingMarker.marker,
          marker
        );

        if (isDifferentMarkerContent) {
          log('Replacing marker', { key: marker.key });

          const newMapMarker = this.integration.replaceMarker(
            existingMarker.mapMarker,
            marker,
            listeners
          );

          this.existingMarkers[marker.key] = {
            marker,
            mapMarker: newMapMarker,
          };
        }
      }
    }

    if (showAllMarkers && thereAreDifferentMarkers) {
      this.showAllMarkers();
    }

    log('End setting markers', this.existingMarkers);
  }

  public isExistingRoute(route: Route): boolean {
    if (!this.existingRoute || this.existingRoute.length === 0) {
      return false;
    }

    const newRouteKey = this.getRouteKey(route);
    const existingRouteKey = this.getRouteKey(this.existingRoute);

    return newRouteKey === existingRouteKey;
  }

  public clearRoutes(): void {
    this.integration.clearRoutes();
  }

  public async addRoute(route: Route): Promise<void> {
    if (!route.length) {
      this.existingRoute = [];
      return;
    }

    const routeInSegmentsByConnection =
      this.getRouteInSegmentsByConnection(route);

    const promises = routeInSegmentsByConnection.map(async (segment) => {
      assertValue(segment[0], 'maps.ts > addRoute > segment[0]');
      const firstConnection = segment[0].type;

      switch (firstConnection) {
        case ConnectionType.NONE:
          return [];

        case ConnectionType.FERRY:
        case ConnectionType.FLIGHT: {
          segment.forEach((pieceOfSegment) => {
            this.integration.addLine(
              pieceOfSegment.from.position,
              pieceOfSegment.to.position
            );
          });

          return [];
        }

        default: {
          const from = segment[0].from;
          const lastSegment = segment[segment.length - 1];
          assertValue(lastSegment, 'maps.ts > addRoute > lastSegment');
          const to = lastSegment.to;
          const waypoints: Marker[] = this.getWayPointsFromSegment(segment);

          return await this.integration.addRoute(from, to, waypoints);
        }
      }
    });

    const awaitedPromises = await Promise.all(promises);

    const result = flatten(awaitedPromises);

    this.existingRoute = result;
  }

  private getWayPointsFromSegment(segment: Route): Marker[] {
    const waypoints: Marker[] = [];
    if (segment.length > 1) {
      for (let i = 0; i < segment.length - 1; i++) {
        const segmentByIndex = segment[i];
        assertValue(segmentByIndex, 'maps.ts > addRoute > segmentByIndex');
        waypoints.push(segmentByIndex.to);
      }
    }
    return waypoints;
  }

  private getRouteInSegmentsByConnection(route: Route): Route[] {
    return route.reduce((connectionsInSegments, connection): Route[] => {
      if (
        !connectionsInSegments.length ||
        this.latestConnectionOrConnectionTypeStoredDoNotMatchWithCurrent(
          connectionsInSegments,
          connection
        )
      ) {
        connectionsInSegments.push([]);
      }

      const last = connectionsInSegments[connectionsInSegments.length - 1];
      assertValue(last, 'maps.ts > addRoute > last');
      last.push(connection);

      return connectionsInSegments;
    }, [] as Route[]);
  }

  private latestConnectionOrConnectionTypeStoredDoNotMatchWithCurrent(
    connectionsInSegmentsByType: Route[],
    currentConnection: RouteConnection
  ): boolean {
    const lastSegmentIndex = connectionsInSegmentsByType.length - 1;
    const lastSegment = connectionsInSegmentsByType[lastSegmentIndex];
    assertValue(
      lastSegment,
      'maps.ts > latestConnectionOrConnectionTypeStoredDoNotMatchWithCurrent > lastSegment'
    );
    const lastConnectionInLastSegmentIndex = lastSegment.length - 1;
    const lastConnection = lastSegment[lastConnectionInLastSegmentIndex];
    assertValue(
      lastConnection,
      'maps.ts > latestConnectionOrConnectionTypeStoredDoNotMatchWithCurrent > lastConnection'
    );

    return (
      lastConnection.type !== currentConnection.type ||
      lastConnection.to.key !== currentConnection.from.key
    );
  }

  private async showAllMarkers(): Promise<void> {
    this.isMovingMapInCodeCounter++;
    const mapMarkers = lodashMap(this.existingMarkers, (x) => x.mapMarker);
    await this.integration.showMarkers(mapMarkers);
    this.isMovingMapInCodeCounter--;
  }

  private getRouteKey(route: Route): string {
    return route.reduce(
      (accumulatedKey, connection) =>
        (accumulatedKey += `${JSON.stringify(
          connection.from.position
        )}-${JSON.stringify(connection.to.position)}-${connection.type}`),
      ''
    );
  }
}

export async function createMap<T extends MapIntegrationType>(
  mapIntegration: MapDynamicIntegration<T>,
  element: MapParent,
  options?: MapInitializationOptions
): Promise<Map<T>> {
  trackMapLoad(element);
  return new Map<T>(
    await mapIntegration.createDynamicMapIntegration(element, options)
  );
}

export async function createStaticMap(
  mapIntegration: MapStaticIntegration,
  element: MapParent,
  options?: StaticMapOptions
): Promise<StaticMap> {
  trackMapLoad(element);
  return new StaticMap(await mapIntegration.createMap(element, options));
}
